import React, { useEffect, useMemo } from "react";

import {
	getGofacSuccessValue,
	getGverifyGauthenticateSuccessValue,
	getMatchSuccessValue,
	getTINCheckSuccessValue,
} from "mappers";
import {
	useAlloyResultQuery,
	useDownloadAlloyDocumentMutation,
	useDownloadGofacDocumentMutation,
	useDownloadGVerifyGAuthenticationDocumentMutation,
	useDownloadMatchDocumentMutation,
	useDownloadTINCheckDocumentMutation,
	useGofacAuroraOutcomeFieldMutation,
	useGofacReRunMutation,
	useGofacResultQuery,
	useGverifyAuroraOutcomeFieldMutation,
	useGVerifyGAuthenticateReRunMutation,
	useGVerifyGAuthenticateResultQuery,
	useMatchAuroraOutcomeFieldMutation,
	useMatchReRunMutation,
	useMatchResultQuery,
	useTINCheckAuroraOutcomeFieldMutation,
	useTinCheckReRunMutation,
	useTINCHeckResultQuery,
} from "queries";
import { AuroraOutcomeStatuses, matchResponseDataType } from "types";

import { CheckListRow } from "./checkListRow";
import MerchantsInformation from "./MerchantOwnersComponents/merchantsInformation";

import styles from "./autoCheckList.module.scss";

export const Merchant = ({
	applicationId,
	isEditable,
	merchantInfoValues,
	isEditableMerchantFieldOnHistorical,
	isDisabledOutcome,
	setCheckedValues,
	isJarisApplication,
}) => {
	const {
		data: dataGofac,
		isFetching: isLoadingDataGofac,
		refetch: dataGofacRefetch,
		isError: isGofacError,
	} = useGofacResultQuery({
		id: applicationId,
	});

	const {
		data: dataGverify,
		isFetching: isLoadingDataGverify,
		refetch: dataGverifyRefetch,
		isError: isGverifyError,
	} = useGVerifyGAuthenticateResultQuery({
		id: applicationId,
	});

	const {
		data: dataMatch,
		isFetching: isLoadingDataMatch,
		refetch: dataMatchRefetch,
		isError: isMatchError,
	} = useMatchResultQuery({
		id: applicationId,
	});

	const {
		data: dataTINCheck,
		isFetching: isLoadingDataTINCheck,
		refetch: dataTINCheckRefetch,
		isError: isTINCheckError,
	} = useTINCHeckResultQuery({
		id: applicationId,
	});

	const {
		data: dataAlloy,
		isFetching: isLoadingDataAlloy,
		isError: isAlloyError,
	} = useAlloyResultQuery({
		id: applicationId,
	});

	const {
		mutate: downloadGOFACDocument,
		isLoading: isLoadingDownloadGOFACDocument,
	} = useDownloadGofacDocumentMutation();

	const {
		mutate: downloadGVerifyDocument,
		isLoading: isLoadingDownloadGVerifyDocument,
	} = useDownloadGVerifyGAuthenticationDocumentMutation();

	const {
		mutate: downloadMatchDocument,
		isLoading: isLoadingDownloadMatchDocument,
	} = useDownloadMatchDocumentMutation();

	const {
		mutate: downloadTINCheckDocument,
		isLoading: isLoadingDownloadTINCheckDocument,
	} = useDownloadTINCheckDocumentMutation();

	const {
		mutate: downloadAlloyDocument,
		isLoading: isLoadingDownloadAlloyCheckDocument,
	} = useDownloadAlloyDocumentMutation();

	const gOFACValues = useMemo(
		() => getGofacSuccessValue(dataGofac),
		[dataGofac],
	);

	const gverifyValues = useMemo(
		() => getGverifyGauthenticateSuccessValue(dataGverify),
		[dataGverify],
	);

	const tinCheckValues = useMemo(
		() => getTINCheckSuccessValue(dataTINCheck),
		[dataTINCheck],
	);

	const matchValues = useMemo(
		() => getMatchSuccessValue(dataMatch as matchResponseDataType),
		[dataMatch],
	);

	useEffect(() => {
		setCheckedValues({
			gVerifyMerchant:
				gverifyValues?.auroraOutcome ===
				AuroraOutcomeStatuses.pass.name,
			gOFACMerchant:
				gOFACValues?.auroraOutcome === AuroraOutcomeStatuses.pass.name,
			matchMerchant:
				matchValues?.auroraOutcome === AuroraOutcomeStatuses.pass.name,
			tinCheckMerchant:
				tinCheckValues?.auroraOutcome ===
				AuroraOutcomeStatuses.pass.name,
		});
	}, [
		gOFACValues?.auroraOutcome,
		gverifyValues?.auroraOutcome,
		matchValues?.auroraOutcome,
		setCheckedValues,
		tinCheckValues?.auroraOutcome,
	]);

	const {
		mutate: submitGverifyAuroraOutcome,
		isLoading: isLoadingGverifyAuroraOutcome,
	} = useGverifyAuroraOutcomeFieldMutation();

	const {
		mutate: submitGofacAuroraOutcome,
		isLoading: isLoadingGofacAuroraOutcome,
	} = useGofacAuroraOutcomeFieldMutation();

	const {
		mutate: submitTINCheckAuroraOutcome,
		isLoading: isLoadingTINCheckAuroraOutcome,
	} = useTINCheckAuroraOutcomeFieldMutation();

	const {
		mutate: submitMatchAuroraOutcome,
		isLoading: isLoadingMatchAuroraOutcome,
	} = useMatchAuroraOutcomeFieldMutation();

	const { mutate: reRunTinCheck, isLoading: isLoadingReRunTinCheck } =
		useTinCheckReRunMutation();

	const { mutate: reRunMatch, isLoading: isLoadingReRunMatch } =
		useMatchReRunMutation();

	const { mutate: reRunGofac, isLoading: isLoadingReRunGofac } =
		useGofacReRunMutation();

	const { mutate: reRunGVerify, isLoading: isLoadingReRunGVerify } =
		useGVerifyGAuthenticateReRunMutation();

	const handleReRunTinCheck = () =>
		reRunTinCheck({
			id: applicationId,
			onSuccessHandler: dataTINCheckRefetch,
		});

	const handleReRunMatch = () =>
		reRunMatch({
			id: applicationId,
			onSuccessHandler: dataMatchRefetch,
		});

	const handleReRunGofac = () =>
		reRunGofac({
			id: applicationId,
			onSuccessHandler: dataGofacRefetch,
		});

	const handleReRunGVerify = () =>
		reRunGVerify({
			id: applicationId,
			onSuccessHandler: dataGverifyRefetch,
		});

	return (
		<div className={styles.wrappAutoCheckListBlock}>
			<div className={styles.merchantBlockTitle}>Merchant</div>
			<CheckListRow
				checkboxLabel="gVerify &amp; gAuthenticate"
				checkboxName="gverify-result"
				checkResult={gverifyValues}
				isLoading={isLoadingDataGverify || isLoadingReRunGVerify}
				downloadDocument={downloadGVerifyDocument}
				isLoadingDownload={isLoadingDownloadGVerifyDocument}
				submitAuroraOutcome={submitGverifyAuroraOutcome}
				isLoadingAuroraOutcome={isLoadingGverifyAuroraOutcome}
				refetchData={dataGverifyRefetch}
				applicationId={applicationId}
				isDisabledOutcome={isDisabledOutcome}
				handleRerun={handleReRunGVerify}
				isDisabledActions={isGverifyError}
			/>

			<CheckListRow
				checkboxLabel="gOFAC"
				checkboxName="merchant-gofac"
				checkResult={gOFACValues}
				isLoading={isLoadingDataGofac || isLoadingReRunGofac}
				downloadDocument={downloadGOFACDocument}
				isLoadingDownload={isLoadingDownloadGOFACDocument}
				submitAuroraOutcome={submitGofacAuroraOutcome}
				isLoadingAuroraOutcome={isLoadingGofacAuroraOutcome}
				refetchData={dataGofacRefetch}
				applicationId={applicationId}
				isDisabledOutcome={isDisabledOutcome}
				handleRerun={handleReRunGofac}
				isDisabledActions={isGofacError}
			/>

			<CheckListRow
				checkboxLabel="MATCH"
				checkboxName="merchantMatch"
				checkResult={matchValues}
				isLoading={isLoadingDataMatch || isLoadingReRunMatch}
				downloadDocument={downloadMatchDocument}
				isLoadingDownload={isLoadingDownloadMatchDocument}
				submitAuroraOutcome={submitMatchAuroraOutcome}
				isLoadingAuroraOutcome={isLoadingMatchAuroraOutcome}
				refetchData={dataMatchRefetch}
				applicationId={applicationId}
				isDisabledOutcome={isDisabledOutcome}
				handleRerun={handleReRunMatch}
				isDisabledActions={isMatchError}
			/>

			<CheckListRow
				checkboxLabel="TIN Check"
				checkboxName="tin-check"
				checkResult={tinCheckValues}
				isLoading={isLoadingDataTINCheck || isLoadingReRunTinCheck}
				downloadDocument={downloadTINCheckDocument}
				isLoadingDownload={isLoadingDownloadTINCheckDocument}
				submitAuroraOutcome={submitTINCheckAuroraOutcome}
				isLoadingAuroraOutcome={isLoadingTINCheckAuroraOutcome}
				refetchData={dataTINCheckRefetch}
				applicationId={applicationId}
				isDisabledOutcome={isDisabledOutcome}
				handleRerun={handleReRunTinCheck}
				isDisabledActions={isTINCheckError}
			/>

			<CheckListRow
				checkboxLabel="Alloy"
				checkboxName="alloy"
				isHiddenCheckResult
				isLoading={isLoadingDataAlloy}
				applicationId={applicationId}
				downloadDocument={downloadAlloyDocument}
				isLoadingDownload={isLoadingDownloadAlloyCheckDocument}
				isDisabledActions={isAlloyError}
				data={dataAlloy}
			/>

			<MerchantsInformation
				merchantInfoValues={merchantInfoValues}
				isEditable={isEditable}
				isEditableMerchantFieldOnHistorical={
					isEditableMerchantFieldOnHistorical
				}
			/>
		</div>
	);
};
